<template>
  <div class="flex h-100 flex-col flex-1">
    <FlotoCrudContainer
      :key="status"
      ref="paginatedCrudRef"
      :fetch-fn="fetchFn"
      :columns="columns"
      as-table
    >
      <template v-slot:add-controls="{ refreshList, items }">
        <FlotoPageHeader>
          <template v-slot:title>
            {{ $tc('my_chat_history') }}
          </template>
          <template v-slot:title-append>
            <FlotoDropdownPicker
              :options="filterOptions"
              :value="(filterType || {}).value"
              class="header-selector search-selector"
              @change="handleFilterChange($event, refreshList)"
            >
              <template v-slot:trigger="{ toggle }">
                <div
                  class="inline-block ml-4 text-neutral cursor-pointer md-text-base font-normal"
                  @click="toggle"
                >
                  {{
                    filterType
                      ? (filterOptions.find((f) => f.key === filterType) || {})
                          .text
                      : $t('all')
                  }}
                  <MIcon name="chevron-down" class="ml-1" />
                </div>
              </template>
            </FlotoDropdownPicker>
          </template>
          <template v-slot>
            <MDivider class="mb-1" />
            <h5 class="inline-block">
              {{ $tc('date') }}
            </h5>
            <MRow class="items-center" :gutter="0">
              <div class="flex flex-1 items-center">
                <FlotoForm
                  ref="formRef"
                  class="flex items-center"
                  @submit="refreshList"
                >
                  <MCol auto-size class="pr-2">
                    <FlotoFormItem
                      id="from-picker"
                      :label="$t('from')"
                      :rules="{
                        // eslint-disable-next-line
                        max_date: endDate,
                      }"
                    >
                      <FlotoDatePicker
                        v-model="startDate"
                        :allow-clear="false"
                        :max-date="endDate"
                      />
                    </FlotoFormItem>
                  </MCol>
                  <MCol auto-size class="px-2">
                    <FlotoFormItem
                      id="to-picker"
                      :rules="{
                        // eslint-disable-next-line
                        min_date: startDate,
                      }"
                      :label="$t('to')"
                    >
                      <FlotoDatePicker
                        v-model="endDate"
                        :allow-clear="false"
                        :min-date="startDate"
                      />
                    </FlotoFormItem>
                  </MCol>
                  <template v-slot:submit>
                    <span />
                  </template>
                </FlotoForm>

                <MButton
                  id="search-btn"
                  class="mt-2 ml-3"
                  :loading="searchLoading"
                  @click="submitForm"
                >
                  {{ $t('search') }}
                </MButton>
                <MButton
                  id="reset-btn"
                  variant="default"
                  class="mt-2 ml-3"
                  @click="resetForm"
                >
                  {{ $t('reset') }}
                </MButton>
              </div>
            </MRow>
          </template>
        </FlotoPageHeader>
      </template>
      <template v-slot:requester="{ item }">
        <td>
          <FlotoUserAvatar
            size="small"
            :avatar="item.user.avatar"
            class="mr-2"
          />
          {{ item.user.name }}
        </td>
      </template>
      <template v-slot:status="{ item }">
        <td>
          {{ (filterOptions.find((f) => f.key === item.status) || {}).text }}
        </td>
      </template>
      <template v-slot:createdAt="{ item }">
        <td>
          {{ item.createdAt | datetime }} ({{ item.createdAt | timeago }})
        </td>
      </template>
      <template v-slot:action="{ item }">
        <td>
          <MButton
            :shadow="false"
            shape="circle"
            variant="neutral-lighter"
            class="mr-2"
            @click="activeRoom = item"
          >
            <MIcon name="eye" />
          </MButton>
        </td>
      </template>
    </FlotoCrudContainer>
    <ChatHistory
      v-if="activeRoom"
      :room="activeRoom"
      :ai-bot="['completed'].indexOf(activeRoom.status) === -1"
      my-chat-history
      @hide="activeRoom = null"
    />
  </div>
</template>

<script>
import Moment from 'moment'
import { getChatChannelsApi } from '@components/chat/chat-api'
import { authComputed } from '@state/modules/auth'
import ChatHistory from '@modules/chat-manager/components/chat-history.vue'

export default {
  name: 'AIBotChatList',
  components: { ChatHistory },
  props: {
    ids: {
      type: Array,
      default() {
        return []
      },
    },
  },
  data() {
    this.filterOptions = [
      { text: this.$t('all'), key: 'all' },
      {
        text: `${this.$tc('technician')} ${this.$tc('chat')}`,
        key: 'completed',
      },
      { text: `${this.$tc('ai_bot')}`, key: 'ai_bot_completed' },
      { text: `${this.$tc('slack')}`, key: 'slack' },
      { text: `${this.$tc('teams')}`, key: 'teams' },
      { text: `${this.$tc('telegram')}`, key: 'telegram' },
      {
        text: `${this.$tc('facebook')} ${this.$tc('messenger')}`,
        key: 'facebook_messenger',
      },
      { text: `${this.$tc('whatsapp')}`, key: 'whatsapp' },
      { text: `${this.$tc('viber')}`, key: 'viber' },
    ]
    this.columns = [
      { key: 'requester', name: this.$tc('requester') },
      { key: 'createdAt', name: this.$tc('date') },
      { key: 'status', name: this.$tc('source') },
      { key: 'action', name: this.$tc('action', 2), width: '250px' },
    ]
    return {
      status: 'ai_bot',
      activeRoom: null,
      filterType: undefined,
      startDate: Moment().subtract(6, 'months').startOf('day').unix() * 1000,
      endDate: Moment().endOf('day').unix() * 1000,
      searchLoading: false,
    }
  },
  computed: {
    ...authComputed,
    supportedStatuses() {
      return this.filterOptions.map((f) => f.key).filter((f) => f !== 'all')
    },
  },
  methods: {
    submitForm() {
      if (this.searchLoading) {
        return
      }
      this.$refs.formRef.submit()
    },
    resetForm() {
      this.startDate =
        Moment().subtract(6, 'months').startOf('day').unix() * 1000
      this.endDate = Moment().endOf('day').unix() * 1000
      setTimeout(() => {
        this.$refs.formRef.submit()
      })
    },
    handleFilterChange(key, refreshList) {
      this.filterType = key === 'all' ? undefined : key
      refreshList()
    },
    fetchFn(limit, offset) {
      this.searchLoading = true
      const filter = {
        userId: [this.user.id],
        startDate: this.startDate,
        endDate: this.endDate,
      }
      if (this.filterType) {
        filter.status = [this.filterType]
      } else {
        filter.status = this.supportedStatuses
      }
      if (this.ids.length) {
        filter.ids = this.ids
      }
      // else {
      //   filter.status = [(this.status, 'ai_bot_completed')]
      // }
      return getChatChannelsApi(filter, limit, offset).finally(
        () => (this.searchLoading = false)
      )
    },
  },
}
</script>
